import uniqBy from 'lodash/uniqBy';
import lowerFirst from 'lodash/lowerFirst';

import {
  Award_Per_Counting_Strategy,
  Earning_Mechanism_Product_Counting_Attribute,
  EarningMechanismFragment,
  EarningMechanismVarietyDiscountFragment,
  EarningMechanismVarietyPurchaseFragment,
  EarningMechanismVarietyPurchaseOverTimeFragment,
  Offer_Engagement_Type,
} from '@occc/graphql';
import { earningMechanismHelper } from '@occc-app/offerHelpers/utils/earningMechanismHelper';
import { awardHelper } from '@occc-app/offerHelpers/utils/awardHelper';
import { LinkChip } from '@occc-app/design-system';
import { ReactNode } from 'react';
import { routeCreator as awardRouteCreator } from '@occc-app/routes/AwardCatalog/utils';
import { formatMoneyAmount } from '@occc-app/lib/currency';
import { pluralize } from '@occc-app/lib/strings';
import { OfferSubtype, OfferType } from '@occc-app/offerValidationService/types';
import { Box } from '@mui/material';
import { AwardFragment } from '@occc-app/offerHelpers/types';
import { formatEnum } from '../formatters';

type SimpleProductGroup = {
  __typename: 'ProductGroup';
  id: string;
  name: string;
};

export const getEarningMechanismDescription = (
  earningMechanism?: EarningMechanismFragment | null,
): string | null => {
  if (!earningMechanism) return null;

  if (earningMechanismHelper.isProductPurchase(earningMechanism)) return 'Product Purchase';
  if (earningMechanismHelper.isProductDiscount(earningMechanism)) return 'Product Discount';
  if (earningMechanismHelper.isTotalSpendOnTransaction(earningMechanism))
    return 'Total Spend on Transaction';
  if (earningMechanismHelper.isTotalEligiblePurchase(earningMechanism))
    return 'Total Eligible Purchase';
  if (earningMechanismHelper.isTransactionDiscount(earningMechanism)) return 'Transaction Discount';
  if (earningMechanismHelper.isTotalSpendOverTime(earningMechanism)) return 'Total Spend Over Time';
  if (earningMechanismHelper.isPointSpend(earningMechanism)) return 'Point Spend';
  if (earningMechanismHelper.isExternalFulfillment(earningMechanism)) return 'External Fulfillment';
  if (earningMechanismHelper.isVisits(earningMechanism)) return 'Visits';
  if (earningMechanismHelper.isVarietyPurchase(earningMechanism)) return 'Variety Purchase';
  if (earningMechanismHelper.isVarietyPurchaseOverTime(earningMechanism))
    return 'Variety Purchase Over Time';
  if (earningMechanismHelper.isVarietyDiscount(earningMechanism)) return 'Variety Discount';
  if (earningMechanismHelper.isLoyaltyEvent(earningMechanism)) return 'Loyalty Event';
  if (earningMechanismHelper.isSpinWheelGame(earningMechanism)) return 'Spin Wheel Game';
  if (earningMechanismHelper.isPromocodes(earningMechanism)) return 'Promo Code';

  return null;
};

/**
 * Returns the purchase quantity as a pluralized label (e.g. "12 items", "1 item")
 * for mechanisms that count items, or null when no item threshold applies.
 */
export const getPurchaseItemsLabel = (
  earningMechanism: EarningMechanismFragment,
): string | null => {
  if (
    earningMechanismHelper.isProductDiscount(earningMechanism) &&
    earningMechanism.purchaseThresholdInt
  ) {
    return `${earningMechanism.purchaseThresholdInt} ${pluralize(
      earningMechanism.purchaseThresholdInt,
      'item',
    )}`;
  }

  if (
    earningMechanismHelper.isVarietyDiscount(earningMechanism) &&
    'purchaseThreshold' in earningMechanism.varietyDiscountAward &&
    earningMechanism.varietyDiscountAward.purchaseThreshold
  ) {
    return `${earningMechanism.varietyDiscountAward.purchaseThreshold} ${pluralize(
      earningMechanism.varietyDiscountAward.purchaseThreshold,
      'item',
    )}`;
  }

  return null;
};

export const getThresholdDescription = (earningMechanism: EarningMechanismFragment): string => {
  const thresholdLabelList: string[] = [];
  const itemsLabel = getPurchaseItemsLabel(earningMechanism);

  if (
    earningMechanismHelper.isProductDiscount(earningMechanism) &&
    earningMechanism.purchaseThresholdInt &&
    itemsLabel
  ) {
    if (awardHelper.isBuyAndGetAward(earningMechanism.productDiscountAward)) {
      thresholdLabelList.push(`Buy ${itemsLabel}`);
    } else if (earningMechanism.purchaseThresholdInt > 1) {
      thresholdLabelList.push(`For ${itemsLabel}`);
    }
  }

  if (earningMechanismHelper.isVarietyDiscount(earningMechanism) && itemsLabel) {
    thresholdLabelList.push(`For ${itemsLabel}`);
  }

  if (
    earningMechanismHelper.isTransactionDiscount(earningMechanism) &&
    earningMechanism.purchaseAmountThreshold
  ) {
    thresholdLabelList.push(
      `When they spend at least ${formatMoneyAmount(earningMechanism.purchaseAmountThreshold, {
        fromCents: true,
      })}`,
    );
  }

  if (
    (earningMechanismHelper.isTotalEligiblePurchaseOverTime(earningMechanism) ||
      earningMechanismHelper.isTotalSpendOverTime(earningMechanism)) &&
    earningMechanism.accumulationThreshold
  ) {
    thresholdLabelList.push(
      `When they spend ${formatMoneyAmount(earningMechanism.accumulationThreshold)}`,
    );
  }

  if (earningMechanismHelper.isVisits(earningMechanism) && earningMechanism.visitThreshold) {
    thresholdLabelList.push(
      `When they make ${earningMechanism.visitThreshold} ${pluralize(
        earningMechanism.visitThreshold,
        'visit',
      )}`,
    );
  }

  return thresholdLabelList.join('. ').trim();
};

export const getNestedAward = (
  earningMechanism: EarningMechanismFragment,
): AwardFragment | null => {
  if (earningMechanismHelper.isProductPurchase(earningMechanism)) {
    return earningMechanism.productPurchaseAward;
  }
  if (earningMechanismHelper.isProductDiscount(earningMechanism)) {
    return earningMechanism.productDiscountAward;
  }
  if (earningMechanismHelper.isTotalSpendOnTransaction(earningMechanism)) {
    return earningMechanism.totalSpendOnTransactionAward;
  }
  if (earningMechanismHelper.isTotalEligiblePurchase(earningMechanism)) {
    return earningMechanism.totalEligiblePurchaseAward;
  }
  if (earningMechanismHelper.isTotalEligiblePurchaseOverTime(earningMechanism)) {
    return earningMechanism.totalEligiblePurchaseOverTimeAward;
  }
  if (earningMechanismHelper.isTransactionDiscount(earningMechanism)) {
    return earningMechanism.transactionDiscountAward;
  }
  if (earningMechanismHelper.isTotalSpendOverTime(earningMechanism)) {
    return earningMechanism.totalSpendOverTimeAward;
  }
  if (earningMechanismHelper.isPointSpend(earningMechanism)) {
    return earningMechanism.pointSpendAward;
  }
  if (earningMechanismHelper.isVisits(earningMechanism)) {
    return earningMechanism.visitsAward;
  }
  if (earningMechanismHelper.isVarietyPurchase(earningMechanism)) {
    return earningMechanism.varietyPurchaseAward;
  }
  if (earningMechanismHelper.isVarietyPurchaseOverTime(earningMechanism)) {
    return earningMechanism.varietyPurchaseOverTimeAward;
  }
  if (earningMechanismHelper.isVarietyDiscount(earningMechanism)) {
    return earningMechanism.varietyDiscountAward;
  }
  if (earningMechanismHelper.isLoyaltyEvent(earningMechanism)) {
    return earningMechanism.loyaltyEventAward ?? null;
  }
  if (earningMechanismHelper.isPromocodes(earningMechanism)) {
    return earningMechanism.award ?? null;
  }
  if (earningMechanismHelper.isExternalFulfillment(earningMechanism)) {
    return earningMechanism.externalFulfillmentAward ?? null;
  }

  return null;
};

const getNestedAwardDescription = (
  award: AwardFragment,
  earningMechanism: EarningMechanismFragment,
  offerType?: OfferType | null,
  offerSubType?: OfferSubtype | null,
): ReactNode => {
  const thresholdDescription = getThresholdDescription(earningMechanism);

  if (awardHelper.isPointsAward(award) || awardHelper.isPointsPerAward(award)) {
    return `${award.points} ${pluralize(award.points, 'Point')} ${lowerFirst(
      thresholdDescription,
    )}`;
  }

  if (awardHelper.isMoneyDiscountAward(award)) {
    const description = `${formatMoneyAmount(award.moneyAmount, {
      fromCents: true,
    })} off ${lowerFirst(thresholdDescription)}`;
    if (offerType === OfferType.FuelRollback || offerSubType === OfferSubtype.FuelRollback) {
      return `${description} per gallon`;
    }
    return description;
  }

  if (awardHelper.isTransactionMoneyDiscountAward(award)) {
    return `${formatMoneyAmount(award.moneyDiscount, {
      fromCents: true,
    })} off transaction ${lowerFirst(thresholdDescription)}`;
  }

  if (awardHelper.isTransactionPercentDiscountAward(award)) {
    return `${award.percentDiscount}% off transaction ${lowerFirst(thresholdDescription)}`;
  }

  if (awardHelper.isVouchersAward(award)) {
    const voucherDescription = [thresholdDescription].filter(Boolean);
    if (award.voucherExpirationDays) {
      voucherDescription.push(
        `${award.voucherExpirationDays} ${pluralize(
          award.voucherExpirationDays,
          'day',
        )} to fulfill after granted`,
      );
    }
    return (
      <>
        <LinkChip
          label={award.voucheredOffer.name || 'Voucher'}
          href={awardRouteCreator.details(award.voucheredOffer.id)}
          tooltip=''
        />
        {!!voucherDescription.length && (
          <Box mt={1} component='span' sx={{ display: 'block' }}>
            {voucherDescription.join('. ')}
          </Box>
        )}
      </>
    );
  }

  if (awardHelper.isPriceOverrideAward(award)) {
    const itemsLabel = getPurchaseItemsLabel(earningMechanism) || 'N/A';

    return award.maximumCost !== 0
      ? `Price override: ${formatMoneyAmount(award.maximumCost, {
          fromCents: true,
        })} ${lowerFirst(thresholdDescription)}`
      : `For every ${itemsLabel} get ${itemsLabel} free`;
  }

  if (awardHelper.isProductDiscountAward(award)) {
    return `${award.percentDiscount}% off ${lowerFirst(thresholdDescription)}`;
  }

  if (awardHelper.isBuyAndGetAward(award)) {
    return `${thresholdDescription} ${thresholdDescription ? 'and get' : ''} ${
      award.discountThreshold
    } ${pluralize(award.discountThreshold, 'item')} for ${
      award.percentDiscount === 100 ? 'Free' : `${award.percentDiscount}% Discount`
    }`;
  }

  return null;
};

export const getAwardDescription = (
  earningMechanism?: EarningMechanismFragment | null,
  earningType?: Offer_Engagement_Type | null,
  offerType?: OfferType | null,
  offerSubType?: OfferSubtype | null,
): ReactNode => {
  if (!earningMechanism) {
    if (earningType === Offer_Engagement_Type.SweepstakesEntry) {
      return 'Sweepstakes entry';
    }
    return null;
  }

  const nestedAward = getNestedAward(earningMechanism);
  if (!nestedAward && earningMechanismHelper.isExternalFulfillment(earningMechanism)) {
    return 'Nothing';
  }

  return nestedAward
    ? getNestedAwardDescription(nestedAward, earningMechanism, offerType, offerSubType)
    : null;
};

export const getProductGroups = (
  earningMechanism?: EarningMechanismFragment | null,
): { eligible: SimpleProductGroup[] | null; ineligible: SimpleProductGroup[] | null } => {
  if (!earningMechanism) return { eligible: null, ineligible: null };

  let eligible: SimpleProductGroup[] | null = null;
  let ineligible: SimpleProductGroup[] | null = null;

  if (
    earningMechanismHelper.isProductPurchase(earningMechanism) ||
    earningMechanismHelper.isProductDiscount(earningMechanism) ||
    earningMechanismHelper.isTotalEligiblePurchase(earningMechanism) ||
    earningMechanismHelper.isTotalEligiblePurchaseOverTime(earningMechanism)
  ) {
    eligible = earningMechanism.eligibleProductGroups ?? [];
  }

  if (
    earningMechanismHelper.isVarietyPurchase(earningMechanism) ||
    earningMechanismHelper.isVarietyPurchaseOverTime(earningMechanism)
  ) {
    eligible = (earningMechanism.products ?? []).flatMap((p) => p.eligibleProductGroups ?? []);
  }

  if (
    earningMechanismHelper.isTotalSpendOnTransaction(earningMechanism) ||
    earningMechanismHelper.isTransactionDiscount(earningMechanism) ||
    earningMechanismHelper.isTotalSpendOverTime(earningMechanism)
  ) {
    ineligible = earningMechanism.ineligibleProductGroups ?? [];
  }

  return {
    eligible: eligible ? uniqBy(eligible, 'id') : null,
    ineligible: ineligible ? uniqBy(ineligible, 'id') : null,
  };
};

export const getPurchaseThreshold = (
  earningMechanism?: EarningMechanismFragment | null,
): number | undefined => {
  if (!earningMechanism) return undefined;

  if (earningMechanismHelper.isProductPurchase(earningMechanism)) {
    return earningMechanism.purchaseThreshold;
  }

  return undefined;
};

export const getCountProductsBy = (
  earningMechanism?: EarningMechanismFragment | null,
): Earning_Mechanism_Product_Counting_Attribute | Award_Per_Counting_Strategy | undefined => {
  if (!earningMechanism) return undefined;

  if (
    earningMechanismHelper.isProductPurchase(earningMechanism) ||
    earningMechanismHelper.isTotalEligiblePurchase(earningMechanism)
  ) {
    return earningMechanism.countProductsBy;
  }

  if (
    earningMechanismHelper.isProductDiscount(earningMechanism) &&
    awardHelper.isMoneyDiscountAward(earningMechanism.productDiscountAward)
  ) {
    return earningMechanism.productDiscountAward.awardPer;
  }

  return undefined;
};

export const getRounding = (
  earningMechanism?: EarningMechanismFragment | null,
): { transaction?: string; products?: string } => {
  if (!earningMechanism) return {};

  let transaction;
  let products;

  if (earningMechanismHelper.isTotalSpendOnTransaction(earningMechanism)) {
    transaction = formatEnum(earningMechanism.roundMoneyAmount.toLowerCase());
  }
  if (earningMechanismHelper.isTotalEligiblePurchase(earningMechanism)) {
    products = formatEnum(earningMechanism.roundCountedProducts.toLowerCase());
  }

  return { transaction, products };
};

export const isVarietyPurchase = (
  earningMechanism?: EarningMechanismFragment | null,
): earningMechanism is
  | EarningMechanismVarietyPurchaseFragment
  | EarningMechanismVarietyPurchaseOverTimeFragment => {
  return (
    !!earningMechanism &&
    (earningMechanismHelper.isVarietyPurchase(earningMechanism) ||
      earningMechanismHelper.isVarietyPurchaseOverTime(earningMechanism))
  );
};

export const isVarietyDiscount = (
  earningMechanism?: EarningMechanismFragment | null,
): earningMechanism is EarningMechanismVarietyDiscountFragment =>
  !!earningMechanism && earningMechanismHelper.isVarietyDiscount(earningMechanism);
